import { useState } from 'react';
import { useTicketTemplates } from '@/hooks/useTicketTemplates';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Settings2, Pencil, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { TicketTemplateForm } from '@/components/settings/TicketTemplateForm';
import { TicketTemplateTasksList } from '@/components/settings/TicketTemplateTasksList';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';

export default function TicketTemplatesSettings() {
  const { templates, isLoading, deleteTemplate } = useTicketTemplates();
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);

  const handleDelete = async (id: string) => {
    await deleteTemplate.mutateAsync(id);
    if (selectedTemplate === id) {
      setSelectedTemplate(null);
    }
  };

  return (
    <div className="container mx-auto py-8 space-y-6 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Templates de Chamados</h1>
          <p className="text-muted-foreground">
            Configure templates para criação automática de chamados e tarefas
          </p>
        </div>
        <Dialog open={isCreateOpen || !!editingTemplate} onOpenChange={(open) => {
          setIsCreateOpen(open);
          if (!open) setEditingTemplate(null);
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Novo Template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingTemplate ? 'Editar' : 'Criar'} Template de Chamado</DialogTitle>
            </DialogHeader>
            <TicketTemplateForm 
              template={editingTemplate}
              onSuccess={() => {
                setIsCreateOpen(false);
                setEditingTemplate(null);
              }} 
            />
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div>Carregando...</div>
      ) : (
        <div className="grid gap-4">
          {templates?.map((template: any) => (
            <Card key={template.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{template.name}</CardTitle>
                    <CardDescription>
                      {template.description}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2 items-center">
                    {template.is_default && (
                      <Badge variant="default">Padrão</Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingTemplate(template)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                            <AlertDialogDescription>
                              Tem certeza que deseja excluir este template? Esta ação não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(template.id)}>
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedTemplate(
                        selectedTemplate === template.id ? null : template.id
                      )}
                    >
                      <Settings2 className="w-4 h-4 mr-2" />
                      {selectedTemplate === template.id ? 'Fechar' : 'Configurar Tarefas'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {selectedTemplate === template.id && (
                <CardContent>
                  <TicketTemplateTasksList templateId={template.id} />
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
